import React, { FunctionComponent, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router';
import { Spinner } from '@vwfs-bronson/bronson-react';
import { App } from './App';
import { SearchedValues } from './screens/Dashboard/subpages/utils/searchedValues.types';
import { checkTokenOnApplicationLoad } from './services/api/authorization';
import { getLogosSrc, getPages, getStyles } from './services/brand';
import { initI18n } from './services/i18n';
import { invalidateStorage } from './services/localStorage';
import { store } from './services/redux';

export type Page = {
  name: string;
  url: string;
  target: string;
  page: number;
  action?: string;
} & SearchedValues;

invalidateStorage();

const Init: FunctionComponent = () => {
  const [loaded, setLoaded] = useState(false);
  const [initialLoginDone, setInitialLoginDone] = useState(false);
  const [initialLoginFailed, setInitialLoginFailed] = useState(false);
  const [isReady, setReady] = useState(false);
  const [pages, setPages] = useState({} as { hybridJourney: Page[]; dealerJourney: Page[] });

  useEffect(() => {
    checkTokenOnApplicationLoad()
      .then(() => {
        setInitialLoginDone(true);
      })
      .catch(() => {
        setInitialLoginDone(true);
        setInitialLoginFailed(true);
      });
  }, []);

  useEffect(() => {
    Promise.all([getStyles(), initI18n()])
      .then(() => {
        setLoaded(true);
      })
      .catch(() => {
        setLoaded(false);
      });
  }, []);

  useEffect(() => {
    if (!initialLoginDone) return;

    Promise.all([getPages(), getLogosSrc()]).then(([_pages]) => {
      setPages(_pages.default);
      setReady(true);
    });
  }, [initialLoginDone]);

  if (!loaded) return null;

  return isReady ? (
    <Provider store={store}>
      <div className="o-root">
        <React.Suspense fallback={<Spinner fullPage />}>
          <BrowserRouter>
            <App pages={pages} initialLoginFailed={initialLoginFailed} />
          </BrowserRouter>
        </React.Suspense>
      </div>
    </Provider>
  ) : (
    <Spinner fullPage />
  );
};

function render(): void {
  const rootElement = document.getElementById('root');

  if (!rootElement) throw new Error('No root element found.');

  const root = createRoot(rootElement);
  root.render(<Init />);
}

declare let module: { hot: any };

if (module.hot) {
  module.hot.accept(render);
}

render();
